/*
 * Generated by tutor-mfe for all.
 *
 * Frontend-base counterpart of the legacy env.config.jsx.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { HexColorPicker } from 'react-colorful';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient, getAuthenticatedUser } from '@edx/frontend-platform/auth';
import {
  ActionRow, Alert, Badge, breakpoints, Button, Card, Form, Icon, ModalPopup, Spinner, useMediaQuery,
} from '@openedx/paragon';
import { Calendar, Edit, Nightlight, WbSunny } from '@openedx/paragon/icons';
import { defineMessages, getMessages, useIntl } from '@edx/frontend-platform/i18n';

// @openedx/frontend-base-compat stands in for @edx/frontend-platform in
// frontend-base site builds and exports only ".", "./auth" and "./i18n".
// logError lives behind "./logging", so shim it rather than import it.
const logError = (error) => { console.error(error); };

import { DIRECT_PLUGIN, IFRAME_PLUGIN, PLUGIN_OPERATIONS } from '@openedx/frontend-plugin-framework';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }
  config.pluginSlots[slot_name].plugins.push(...plugins);
}
let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isDarkVariantEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0D0D0E;
            color: #ccc;
          }
          a { color: #ccc; }
          a:hover { color: #d3d3d3; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isDarkVariantEnabled && theme === 'dark') {
      applyThemeVariant('dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};
const INDIGO_THEME_STORAGE_KEY = 'indigo-user-theme-mfe';
const INDIGO_THEME_STYLE_ID = 'indigo-user-theme';
const INDIGO_THEME_API_PATH = '/api/indigo/v1/theme/';

const INDIGO_THEME_TOKENS = [
  { token: '--pgn-color-primary-base', label: 'Buttons and links' },
  { token: '--pgn-color-primary-light', label: 'Button and link highlights' },
  { token: '--pgn-color-brand-base', label: 'Brand accents' },
  { token: '--pgn-color-text-base', label: 'Body text' },
  { token: '--pgn-color-info-base', label: 'Navigation links' },
];

const INDIGO_TOKEN_MESSAGES = {
  '--pgn-color-primary-base': {
    id: 'theme.settings.token.primary.v2',
    defaultMessage: 'Buttons and links',
    description: 'label of the colour used for buttons and links',
  },
  '--pgn-color-primary-light': {
    id: 'theme.settings.token.primaryLight.v2',
    defaultMessage: 'Button and link highlights',
    description: 'label of the colour used for button and link highlights',
  },
  '--pgn-color-brand-base': {
    id: 'theme.settings.token.secondary.v2',
    defaultMessage: 'Brand accents',
    description: 'label of the colour used for brand accents',
  },
  '--pgn-color-text-base': {
    id: 'theme.settings.token.text.v2',
    defaultMessage: 'Body text',
    description: 'label of the colour used for body text',
  },
  '--pgn-color-info-base': {
    id: 'theme.settings.token.info.v2',
    defaultMessage: 'Navigation links',
    description: 'label of the colour used for navigation links',
  },
};

const INDIGO_THEME_COOKIE_NAMES = [
  'selected-paragon-theme-variant',
  'selected-theme-variant',
];
const INDIGO_THEME_ATTRIBUTE_NAMES = [
  'data-paragon-theme-variant',
  'data-theme-variant',
];
const INDIGO_THEME_COOKIE_EXPIRY_DAYS = 90;
const INDIGO_THEME_JWT_COOKIE_NAME = 'edx-jwt-cookie-header-payload';
const INDIGO_THEME_VARIANT_LINK_PATTERNS = [
  /theme-variants-(light|dark)\b/,
  /\/(light|dark)\.min\.css/,
];

const INDIGO_THEME_FAMILIES = ['primary', 'brand', 'info'];
const INDIGO_THEME_SHADE_MIX = {
  100: ['#FFFFFF', 0.94],
  200: ['#FFFFFF', 0.75],
  300: ['#FFFFFF', 0.50],
  400: ['#FFFFFF', 0.25],
  500: null,
  600: ['#000000', 0.10],
  700: ['#000000', 0.20],
  800: ['#000000', 0.25],
  900: ['#000000', 0.30],
};
const INDIGO_THEME_ICON_TOKENS = {
  '--pgn-other-content-form-control-checkbox-indicator-icon-checked-base':
    "url(\"data:image/svg+xml,<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M21 3H3V21H21V3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z' fill='%%HEX%%'/></svg>\")",
  '--pgn-other-content-form-control-radio-indicator-icon-checked-base':
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%%HEX%%'/%3e%3c/svg%3e\")",
  '--pgn-other-content-form-control-switch-indicator-icon-off':
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%%HEX%%'/%3e%3c/svg%3e\")",
};

const INDIGO_THEME_HEX_RE = /^#[0-9a-fA-F]{6}$/;
const INDIGO_CSS_NAME_RE = /^--[a-z0-9-]+$/;
const INDIGO_THEME_COMPUTED_HEX_RE = /^#([0-9a-fA-F]{6})(?:[0-9a-fA-F]{2})?$/;
const INDIGO_CONTRAST_AA = 4.5;
const INDIGO_THEME_RESET_PARAM = 'indigo-theme-reset';
const INDIGO_THEME_RESET_MESSAGE = {
  id: 'theme.reset.confirm',
  defaultMessage: 'Reset your saved theme colors to the site defaults?',
  description: 'confirmation shown before the reset link clears the saved colours',
};
const INDIGO_THEME_SHARED_COOKIE = 'indigo-theme';
const INDIGO_RUNTIME_POLL_MS = 50;
const INDIGO_RUNTIME_TIMEOUT_MS = 10000;
const INDIGO_CAPTURE_DELAYS_MS = [0, 150, 500, 1200, 2500];

let indigoBaseTokens = {};
let lastCapturedVariant = null;
let baseTokenListeners = [];

const onBaseTokensCaptured = (listener) => {
  baseTokenListeners.push(listener);
  return () => {
    baseTokenListeners = baseTokenListeners.filter((item) => item !== listener);
  };
};

const hexToRgb = (hex) => {
  if (typeof hex !== 'string') {
    return null;
  }
  let value = hex.trim().replace('#', '');
  if (value.length === 3) {
    value = value.split('').map((char) => char + char).join('');
  }
  if (value.length === 8) {
    value = value.slice(0, 6);
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return null;
  }
  const int = parseInt(value, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

const rgbToHex = (rgb) => {
  const channel = (value) => Math.max(0, Math.min(255, Math.round(value)))
    .toString(16)
    .padStart(2, '0');
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`.toUpperCase();
};

const mixColor = (hex, otherHex, amount) => {
  const base = hexToRgb(hex);
  const other = hexToRgb(otherHex);
  if (!base || !other) {
    return hex;
  }
  return rgbToHex({
    r: base.r * (1 - amount) + other.r * amount,
    g: base.g * (1 - amount) + other.g * amount,
    b: base.b * (1 - amount) + other.b * amount,
  });
};

const rgbToHsl = (rgb) => {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h, s, l };
};

const hslToRgb = (hsl) => {
  const { h, s, l } = hsl;
  if (s === 0) {
    return { r: l * 255, g: l * 255, b: l * 255 };
  }
  const hueToChannel = (p, q, hue) => {
    let t = hue;
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
      return q;
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hueToChannel(p, q, h + 1 / 3) * 255,
    g: hueToChannel(p, q, h) * 255,
    b: hueToChannel(p, q, h - 1 / 3) * 255,
  };
};

const darkenColor = (hex, amount) => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return hex;
  }
  const hsl = rgbToHsl(rgb);
  hsl.l = Math.max(0, hsl.l - amount);
  return rgbToHex(hslToRgb(hsl));
};

const luminanceFromRgb = (rgb) => {
  const channel = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  return rgb ? luminanceFromRgb(rgb) : null;
};

const rgbToHsv = (rgb) => {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h *= 60;
    if (h < 0) {
      h += 360;
    }
  }
  return { h, s: max ? delta / max : 0, v: max };
};

const hsvToRgb = (h, s, v) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let parts = [c, 0, x];
  if (h < 60) {
    parts = [c, x, 0];
  } else if (h < 120) {
    parts = [x, c, 0];
  } else if (h < 180) {
    parts = [0, c, x];
  } else if (h < 240) {
    parts = [0, x, c];
  } else if (h < 300) {
    parts = [x, 0, c];
  }
  return { r: (parts[0] + m) * 255, g: (parts[1] + m) * 255, b: (parts[2] + m) * 255 };
};

const contrastGuidePoints = (hue, threshold, backgroundHex) => {
  const background = relativeLuminance(backgroundHex);
  if (background === null) {
    return '';
  }
  const ratioAt = (s, v) => {
    const value = luminanceFromRgb(hsvToRgb(hue, s, v));
    return (Math.max(value, background) + 0.05) / (Math.min(value, background) + 0.05);
  };
  const points = [];
  const samples = 24;
  for (let i = 0; i <= samples; i += 1) {
    const s = i / samples;
    if ((ratioAt(s, 0) - threshold) * (ratioAt(s, 1) - threshold) <= 0) {
      let low = 0;
      let high = 1;
      for (let step = 0; step < 16; step += 1) {
        const mid = (low + high) / 2;
        if ((ratioAt(s, mid) - threshold) * (ratioAt(s, low) - threshold) <= 0) {
          high = mid;
        } else {
          low = mid;
        }
      }
      const v = (low + high) / 2;
      points.push(`${(s * 100).toFixed(2)},${((1 - v) * 100).toFixed(2)}`);
    }
  }
  return points.length > 1 ? points.join(' ') : '';
};

const contrastRatio = (hex, otherHex) => {
  const a = relativeLuminance(hex);
  const b = relativeLuminance(otherHex);
  if (a === null || b === null) {
    return null;
  }
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

const readableTextOn = (hex) => (
  (contrastRatio(hex, '#FFFFFF') || 0) >= INDIGO_CONTRAST_AA ? '#FFFFFF' : '#111827'
);

const normalizeHexToken = (value) => {
  const match = INDIGO_THEME_COMPUTED_HEX_RE.exec((value || '').trim());
  return match ? `#${match[1].toUpperCase()}` : null;
};

const liveTokenDefaults = () => {
  const result = {};
  try {
    const computed = window.getComputedStyle(document.documentElement);
    INDIGO_THEME_TOKENS.forEach((entry) => {
      const value = normalizeHexToken(computed.getPropertyValue(entry.token));
      if (value) {
        result[entry.token] = value;
      }
    });
  } catch (error) {}
  return result;
};

const captureBaseTokens = (variantHint) => {
  try {
    const variant = variantHint
      || document.documentElement.getAttribute(INDIGO_THEME_ATTRIBUTE_NAMES[0])
      || getStoredThemeVariant()
      || 'light';
    const style = document.getElementById(INDIGO_THEME_STYLE_ID);
    const wasDisabled = style ? style.disabled : false;
    if (style) {
      style.disabled = true;
    }
    const tokens = liveTokenDefaults();
    if (style) {
      style.disabled = wasDisabled;
    }
    if (!Object.keys(tokens).length) {
      return;
    }
    if (JSON.stringify(indigoBaseTokens[variant]) === JSON.stringify(tokens)) {
      return;
    }
    indigoBaseTokens[variant] = tokens;
    baseTokenListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {}
    });
  } catch (error) {}
};

const recaptureBaseTokens = (variant) => {
  INDIGO_CAPTURE_DELAYS_MS.forEach((delay) => {
    window.setTimeout(() => captureBaseTokens(variant), delay);
  });
};

const baseTokensFor = (variant) => indigoBaseTokens[variant] || {};

const buildThemeTokens = (tokens) => {
  const result = { ...(tokens || {}) };
  INDIGO_THEME_FAMILIES.forEach((family) => {
    const base = result[`--pgn-color-${family}-base`];
    if (!hexToRgb(base)) {
      return;
    }
    result[`--pgn-color-action-default-${family}-base`] = darkenColor(base, 0.1);
    Object.keys(INDIGO_THEME_SHADE_MIX).forEach((shade) => {
      const mix = INDIGO_THEME_SHADE_MIX[shade];
      const value = mix ? mixColor(base, mix[0], mix[1]) : rgbToHex(hexToRgb(base));
      result[`--pgn-color-${family}-${shade}`] = value;
      result[`--pgn-color-action-default-${family}-${shade}`] = darkenColor(value, 0.1);
    });
  });
  const primary = result['--pgn-color-primary-base'];
  if (hexToRgb(primary)) {
    const encoded = `%23${primary.replace('#', '').toUpperCase()}FF`;
    Object.keys(INDIGO_THEME_ICON_TOKENS).forEach((name) => {
      result[name] = INDIGO_THEME_ICON_TOKENS[name].replace('%%HEX%%', encoded);
    });
    result['--pgn-color-btn-text-primary'] = readableTextOn(primary);
  }
  return result;
};

const buildThemeCss = (tokens) => {
  const built = buildThemeTokens(tokens);
  const declarations = Object.keys(built)
    .filter((name) => INDIGO_CSS_NAME_RE.test(name))
    .map((name) => `${name}:${built[name]};`)
    .join('');
  return `:root:root{${declarations}}`;
};

const applyThemeTokens = (tokens) => {
  try {
    let style = document.getElementById(INDIGO_THEME_STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = INDIGO_THEME_STYLE_ID;
    }
    style.textContent = buildThemeCss(tokens);
    document.head.appendChild(style);
  } catch (error) {}
};

const clearThemeTokens = () => {
  try {
    const style = document.getElementById(INDIGO_THEME_STYLE_ID);
    if (style) {
      style.remove();
    }
  } catch (error) {}
};

const getStoredThemeVariant = () => {
  try {
    for (const name of INDIGO_THEME_COOKIE_NAMES) {
      const value = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`))
        ?.split('=')[1];
      if (value !== undefined && value !== '' && value !== 'undefined') {
        return value;
      }
    }
    for (const name of INDIGO_THEME_COOKIE_NAMES) {
      const value = window.localStorage.getItem(name);
      if (value && value !== 'undefined') {
        return value;
      }
    }
  } catch (error) {}
  return undefined;
};

const themeVariantLinkName = (href) => {
  for (const pattern of INDIGO_THEME_VARIANT_LINK_PATTERNS) {
    const match = pattern.exec(href);
    if (match) {
      return match[1];
    }
  }
  return null;
};

const syncThemeVariantStylesheets = (variant) => {
  try {
    document.querySelectorAll('link[rel*="stylesheet"]').forEach((link) => {
      const name = themeVariantLinkName(link.getAttribute('href') || '');
      if (!name) {
        return;
      }
      link.setAttribute('rel', name === variant ? 'stylesheet' : 'alternate stylesheet');
    });
  } catch (error) {}
};

const notifyThemeVariantIframes = (variant) => {
  try {
    document.querySelectorAll('iframe').forEach((frame) => {
      frame.contentWindow?.postMessage({ 'indigo-toggle-dark': variant }, '*');
    });
  } catch (error) {}
};

const applyThemeVariant = (variant) => {
  try {
    INDIGO_THEME_ATTRIBUTE_NAMES.forEach((attribute) => {
      document.documentElement.setAttribute(attribute, variant);
    });
  } catch (error) {}
  syncThemeVariantStylesheets(variant);
};

const appliedThemeVariant = () => {
  const values = INDIGO_THEME_ATTRIBUTE_NAMES
    .map((attribute) => document.documentElement.getAttribute(attribute))
    .filter(Boolean);
  if (values.length !== INDIGO_THEME_ATTRIBUTE_NAMES.length) {
    return null;
  }
  return values.every((value) => value === values[0]) ? values[0] : null;
};

const watchThemeVariantAttributes = () => {
  try {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      const primary = root.getAttribute(INDIGO_THEME_ATTRIBUTE_NAMES[0]);
      if (!primary) {
        return;
      }
      INDIGO_THEME_ATTRIBUTE_NAMES.slice(1).forEach((attribute) => {
        if (root.getAttribute(attribute) !== primary) {
          root.setAttribute(attribute, primary);
        }
      });
      if (primary !== lastCapturedVariant) {
        lastCapturedVariant = primary;
        recaptureBaseTokens(primary);
      }
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: INDIGO_THEME_ATTRIBUTE_NAMES,
    });
  } catch (error) {}
};

const persistThemeVariant = (variant) => {
  try {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const today = new Date();
    const expires = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + INDIGO_THEME_COOKIE_EXPIRY_DAYS,
    );
    INDIGO_THEME_COOKIE_NAMES.forEach((name) => {
      document.cookie = `${name}=${variant}; domain=${serverURL.hostname}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
      window.localStorage.setItem(name, variant);
    });
  } catch (error) {}
  applyThemeVariant(variant);
  notifyThemeVariantIframes(variant);
};

const getCookieUsername = () => {
  try {
    const name = getConfig().ACCESS_TOKEN_COOKIE_NAME || INDIGO_THEME_JWT_COOKIE_NAME;
    const value = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.slice(name.length + 1);
    if (!value) {
      return null;
    }
    const payload = value.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(payload)).preferred_username || null;
  } catch (error) {
    return null;
  }
};

const clearThemeCache = () => {
  try {
    window.localStorage.removeItem(INDIGO_THEME_STORAGE_KEY);
    window.localStorage.removeItem('indigo-user-theme');
  } catch (error) {}
};

const readThemeCache = () => {
  try {
    const raw = window.localStorage.getItem(INDIGO_THEME_STORAGE_KEY);
    const cache = raw ? JSON.parse(raw) : null;
    const username = getCookieUsername();
    if (!cache || !username || cache.username !== username) {
      clearThemeCache();
      return null;
    }
    return cache;
  } catch (error) {
    return null;
  }
};

const writeThemeCache = (payload) => {
  writeSharedTheme(payload);
  try {
    const username = getAuthenticatedUser()?.username || getCookieUsername();
    if (!username) {
      clearThemeCache();
      return;
    }
    window.localStorage.setItem(INDIGO_THEME_STORAGE_KEY, JSON.stringify({ ...payload, username }));
  } catch (error) {}
};

const sharedThemeFromPayload = (payload) => {
  const preset = (payload?.presets || []).find((item) => item.slug === payload?.preset);
  const defaults = preset?.tokens || {};
  const overrides = (payload?.overrides || {})[payload?.preset] || {};
  const colors = {};
  Object.keys(overrides).forEach((token) => {
    const to = overrides[token];
    if (!INDIGO_THEME_HEX_RE.test(to || '')) {
      return;
    }
    colors[token] = [String(defaults[token] || '').toUpperCase(), to.toUpperCase()];
  });
  return {
    u: getAuthenticatedUser()?.username || getCookieUsername() || '',
    v: payload?.variant || '',
    p: payload?.preset || '',
    c: colors,
  };
};

const writeSharedTheme = (payload) => {
  try {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const today = new Date();
    const expires = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + INDIGO_THEME_COOKIE_EXPIRY_DAYS,
    );
    const value = encodeURIComponent(JSON.stringify(sharedThemeFromPayload(payload)));
    document.cookie = `${INDIGO_THEME_SHARED_COOKIE}=${value}; domain=${serverURL.hostname}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
  } catch (error) {}
};

const clearSharedTheme = () => {
  try {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    document.cookie = `${INDIGO_THEME_SHARED_COOKIE}=; domain=${serverURL.hostname}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
  } catch (error) {}
};

const readSharedTheme = () => {
  try {
    const raw = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${INDIGO_THEME_SHARED_COOKIE}=`))
      ?.slice(INDIGO_THEME_SHARED_COOKIE.length + 1);
    if (!raw) {
      return null;
    }
    const shared = JSON.parse(decodeURIComponent(raw));
    return shared && typeof shared === 'object' ? shared : null;
  } catch (error) {
    return null;
  }
};

const INDIGO_CUSTOMIZABLE_TOKENS = INDIGO_THEME_TOKENS.map((entry) => entry.token);

const sharedThemeTokens = (shared) => {
  const result = {};
  Object.keys(shared?.c || {}).forEach((token) => {
    const pair = shared.c[token];
    if (!INDIGO_CUSTOMIZABLE_TOKENS.includes(token) || !Array.isArray(pair)) {
      return;
    }
    if (INDIGO_THEME_HEX_RE.test(pair[1] || '')) {
      result[token] = pair[1];
    }
  });
  return result;
};

const effectiveTokensFor = (payload, presetSlug) => {
  const overrides = (payload?.overrides || {})[presetSlug] || {};
  const result = {};
  Object.keys(overrides).forEach((token) => {
    if (overrides[token]) {
      result[token] = overrides[token];
    }
  });
  return result;
};

let indigoThemeRequest = null;

const resetUserThemeRequest = () => {
  indigoThemeRequest = null;
};

const fetchUserTheme = () => {
  if (!indigoThemeRequest) {
    indigoThemeRequest = getAuthenticatedHttpClient()
      .get(`${getConfig().LMS_BASE_URL}${INDIGO_THEME_API_PATH}`)
      .catch((error) => {
        resetUserThemeRequest();
        throw error;
      });
  }
  return indigoThemeRequest;
};

const saveThemeVariant = (variant) => {
  if (!getConfig().INDIGO_ENABLE_DYNAMIC_THEME || !getAuthenticatedUser()) {
    return Promise.resolve();
  }
  return fetchUserTheme()
    .then(({ data }) => {
      const preset = (data?.presets || []).find((item) => item.variant === variant);
      if (!preset || preset.slug === data.preset) {
        return null;
      }
      return getAuthenticatedHttpClient()
        .patch(`${getConfig().LMS_BASE_URL}${INDIGO_THEME_API_PATH}`, { preset: preset.slug })
        .then(({ data: updated }) => {
          resetUserThemeRequest();
          writeThemeCache(updated);
          return updated;
        });
    })
    .catch((error) => {
      logError(error);
    });
};

const configIfReady = () => {
  let config = null;
  try {
    config = getConfig() || null;
  } catch (error) {
    return null;
  }
  return config && config.LMS_BASE_URL ? config : null;
};

const authIfReady = () => {
  try {
    return getAuthenticatedHttpClient() ? true : null;
  } catch (error) {
    return null;
  }
};

const whenReady = (probe) => new Promise((resolve) => {
  const started = Date.now();
  const check = () => {
    const value = probe();
    if (value) {
      resolve(value);
      return;
    }
    if (Date.now() - started > INDIGO_RUNTIME_TIMEOUT_MS) {
      resolve(null);
      return;
    }
    window.setTimeout(check, INDIGO_RUNTIME_POLL_MS);
  };
  check();
});

const translate = (message) => {
  try {
    return getMessages()[message.id] || message.defaultMessage;
  } catch (error) {
    return message.defaultMessage;
  }
};

const resetRequested = () => {
  try {
    return new URLSearchParams(window.location.search).has(INDIGO_THEME_RESET_PARAM);
  } catch (error) {
    return false;
  }
};

const dropResetParam = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(INDIGO_THEME_RESET_PARAM);
    window.location.replace(url.toString());
  } catch (error) {}
};

const resetStoredTheme = () => getAuthenticatedHttpClient()
  .delete(`${getConfig().LMS_BASE_URL}${INDIGO_THEME_API_PATH}`)
  .then(() => {
    resetUserThemeRequest();
    clearThemeTokens();
    clearThemeCache();
    clearSharedTheme();
  })
  .catch((error) => {
    logError(error);
  })
  .then(dropResetParam);

const adoptThemeVariant = (variant, data) => {
  const preset = (data?.presets || []).find((item) => item.variant === variant);
  if (!preset) {
    return;
  }
  persistThemeVariant(variant);
  getAuthenticatedHttpClient()
    .patch(`${getConfig().LMS_BASE_URL}${INDIGO_THEME_API_PATH}`, { preset: preset.slug })
    .then(({ data: updated }) => {
      resetUserThemeRequest();
      writeThemeCache(updated);
      applyThemeTokens(effectiveTokensFor(updated, updated.preset));
    })
    .catch((error) => {
      logError(error);
    });
};

const applyThemePayload = (data) => {
  writeThemeCache(data);
  const applied = appliedThemeVariant();
  const browserVariant = getStoredThemeVariant() || applied;
  if (!data.stored && browserVariant && data.variant && browserVariant !== data.variant) {
    adoptThemeVariant(browserVariant, data);
    return;
  }
  if (data.variant && data.variant !== applied) {
    persistThemeVariant(data.variant);
  }
  applyThemeTokens(effectiveTokensFor(data, data.preset));
};

let indigoThemeStarted = false;

const startIndigoTheme = () => {
  if (indigoThemeStarted) {
    return;
  }
  indigoThemeStarted = true;
  watchThemeVariantAttributes();
  recaptureBaseTokens();
  window.addEventListener('load', () => recaptureBaseTokens());
  whenReady(configIfReady).then((config) => {
    if (!config || config.INDIGO_ENABLE_DYNAMIC_THEME === false) {
      clearThemeTokens();
      clearThemeCache();
      clearSharedTheme();
      resetUserThemeRequest();
      return;
    }

    const username = getCookieUsername();
    const shared = readSharedTheme();
    const sharedIsUsable = Boolean(shared) && (!username || shared.u === username);

    if (sharedIsUsable) {
      applyThemeTokens(sharedThemeTokens(shared));
      if (!username && shared.v && (shared.v !== 'dark' || config.INDIGO_ENABLE_DARK_TOGGLE)) {
        applyThemeVariant(shared.v);
      }
    } else {
      clearThemeTokens();
    }

    if (!username) {
      clearThemeCache();
      resetUserThemeRequest();
      return;
    }

    if (resetRequested()) {
      const prompt = translate(INDIGO_THEME_RESET_MESSAGE);
      if (window.confirm(prompt)) {
        resetStoredTheme();
      } else {
        dropResetParam();
      }
      return;
    }

    const cache = readThemeCache();
    if (cache && cache.preset) {
      applyThemeTokens(effectiveTokensFor(cache, cache.preset));
    }
    whenReady(authIfReady).then((ready) => {
      if (!ready) {
        return;
      }
      fetchUserTheme()
        .then(({ data }) => {
          applyThemePayload(data);
        })
        .catch((error) => {
          logError(error);
        });
    });
  });
};

startIndigoTheme();

const ApplyUserTheme = () => {
  useEffect(() => {
    startIndigoTheme();
  }, []);

  return (<div />);
};

function setConfig() {
  const config = {
    pluginSlots: {},
  };
const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();

  const indigoFooterNavLinks = config.INDIGO_FOOTER_NAV_LINKS || [];

  const messages = {
    "footer.poweredby.text": {
      id: "footer.poweredby.text",
      defaultMessage: "Powered by",
      description: "text for the footer",
    },
    "footer.tutorlogo.altText": {
      id: "footer.tutorlogo.altText",
      defaultMessage: "Runs on Tutor",
      description: "alt text for the footer tutor logo",
    },
    "footer.logo.altText": {
      id: "footer.logo.altText",
      defaultMessage: "Powered by Open edX",
      description: "alt text for the footer logo.",
    },
    "footer.copyright.text": {
      id: "footer.copyright.text",
      defaultMessage: `Copyrights ©${new Date().getFullYear()}. All Rights Reserved.`,
      description: "copyright text for the footer",
    },
  };

  return (
    <div className="wrapper wrapper-footer">
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <div className="powered-area">
            <ul className="logo-list">
              <li>{intl.formatMessage(messages["footer.poweredby.text"])}</li>
              <li>
                <a
                  href="https://edly.io/tutor/"
                  rel="noreferrer"
                  target="_blank"
                >
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/tutor-logo.png`}
                    alt={intl.formatMessage(
                      messages["footer.tutorlogo.altText"]
                    )}
                    width="57"
                  />
                </a>
              </li>
              <li>
                <a href="https://open.edx.org" rel="noreferrer" target="_blank">
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/openedx-logo.png`}
                    alt={intl.formatMessage(messages["footer.logo.altText"])}
                    width="79"
                  />
                </a>
              </li>
            </ul>
          </div>
          <nav className="nav-colophon">
            <ol>
              {indigoFooterNavLinks.map((link) => (
                <li key={link.url}>
                  <a href={`${link.url.startsWith("http") ? link.url : config.LMS_BASE_URL + link.url}`}>{link.title}</a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">
          {intl.formatMessage(messages["footer.copyright.text"])}
        </span>
      </footer>
    </div>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt="Open edX" />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt="Open edX" />
      </a>
    </>
  );
};
const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(
    () => getStoredThemeVariant() === 'dark',
  );

  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const onToggleTheme = () => {
    const theme = getStoredThemeVariant() === 'dark' ? 'light' : 'dark';
    setIsDarkThemeEnabled(theme === 'dark');
    persistThemeVariant(theme);

    saveThemeVariant(theme).then(() => {
      window.location.reload();
    });
  };

  useEffect(() => {
    const variant = getStoredThemeVariant();
    if (!variant) {
      return;
    }
    if (variant !== window.localStorage.getItem(INDIGO_THEME_COOKIE_NAMES[0])) {
      persistThemeVariant(variant);
      window.location.reload();
    }
    applyThemeVariant(variant);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDarkThemeEnabled(
        root.getAttribute(INDIGO_THEME_ATTRIBUTE_NAMES[0]) === 'dark',
      );
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: INDIGO_THEME_ATTRIBUTE_NAMES,
    });
    return () => observer.disconnect();
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            checked={isDarkThemeEnabled}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"} Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};
const getFullImageUrl = (path) => {
  if (!path) {
    return '';
  }
  return path.startsWith('http') ? path : `${getConfig().LMS_BASE_URL}${path}`;
};

const DATE_FORMAT_OPTIONS = { month: 'short', day: 'numeric', year: 'numeric' };

const messages = {
  startDate: {
    id: 'generic.course-card.start-date',
    defaultMessage: 'Starts: {startDate}',
    description: 'Start date.',
  }
};

const formatDate = (dateString, intl) => {
  const date = new Date(dateString);
  return intl.formatDate(date, DATE_FORMAT_OPTIONS);
};

const getStartDateDisplay = (courseData, intl) => {
  if (courseData?.advertisedStart) {
    return courseData.advertisedStart;
  }

  if (courseData?.start) {
    return formatDate(courseData.start, intl);
  }

  return '';
};

const CourseCard = ({
  isLoading,
  courseId,
  courseOrg,
  courseName,
  courseNumber,
  courseImageUrl,
  courseStartDate,
  courseAdvertisedStart,
}) => {
  const intl = useIntl();
  const isExtraSmall = useMediaQuery({ maxWidth: breakpoints.small.maxWidth });
  const noCourseImg = `${getConfig().LMS_BASE_URL}/theming/asset/images/no_course_image.png`;
  const startDateDisplay = (courseStartDate || courseAdvertisedStart) ? getStartDateDisplay({
    start: courseStartDate,
    advertisedStart: courseAdvertisedStart,
  }, intl) : null;

  return (
    <Card
      className={`course-card d-flex ${isExtraSmall ? 'w-100' : 'course-card-desktop'}`}
      isLoading={isLoading}
      data-testid="course-card"
    >
      <Card.ImageCap
        src={getFullImageUrl(courseImageUrl)}
        fallbackSrc={noCourseImg}
        srcAlt={`${courseName} ${courseNumber}`}
        skeletonDuringImageLoad
      />
      <div className="px-4 py-2 text-left x-small">{courseNumber}</div>
      <Card.Header
        title={courseName}
        subtitle={(
          <div>{courseOrg}</div>
        )}
        size="sm"
      />
      <Card.Section />
      {!isLoading && (
        <Button
          as={Link}
          to={courseId ? `/courses/${courseId}/about` : undefined}
          className="mx-4 mb-4"
        >Learn More</Button>
      )}
      <Card.Footer className="justify-content-start py-3">
        <Icon className="mr-2" src={Calendar} aria-label={intl.formatMessage(messages.startDate)} />
        {startDateDisplay && intl.formatMessage(messages.startDate, {
          startDate: startDateDisplay,
        })}
      </Card.Footer>
    </Card>
  );
};
const INDIGO_THEME_SETTINGS_STYLE_ID = 'indigo-theme-settings-css';

const INDIGO_THEME_SETTINGS_CSS = `
#theme-settings .indigo-theme-tiles {
  align-items: stretch;
  display: flex;
  flex-flow: row wrap;
  gap: .75rem;
  max-width: none;
}
#theme-settings .indigo-theme-tile {
  align-items: center;
  background: var(--pgn-color-body-bg, transparent);
  border: 1px solid var(--pgn-color-border, var(--pgn-color-form-control-indicator-border, #c4c7cf));
  border-radius: .375rem;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  margin: 0;
  max-width: 100%;
  padding: 0;
  position: relative;
  width: 220px;
}
#theme-settings .indigo-theme-tile-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
#theme-settings .indigo-theme-tile input[type="radio"] {
  height: 1px;
  margin: 0;
  opacity: 0;
  position: absolute;
  width: 1px;
}
#theme-settings .indigo-theme-tile > div { width: 100%; }
#theme-settings .indigo-theme-tile label {
  align-items: center;
  cursor: pointer;
  display: flex;
  margin: 0;
  padding: .75rem 1rem;
}
#theme-settings .indigo-theme-tile--selected {
  border-color: var(--pgn-color-primary-base, #15376D);
  box-shadow: inset 0 0 0 1px var(--pgn-color-primary-base, #15376D);
}
#theme-settings .indigo-theme-tile:hover {
  border-color: var(--pgn-color-primary-base, #15376D);
}
#theme-settings .indigo-theme-tile:has(input:disabled),
#theme-settings .indigo-theme-tile:has(input:disabled) label {
  cursor: default;
}
#theme-settings .indigo-theme-tile:has(input:disabled):not(.indigo-theme-tile--selected):hover {
  border-color: var(--pgn-color-border, var(--pgn-color-form-control-indicator-border, #c4c7cf));
}
#theme-settings .indigo-theme-tile:focus-within {
  outline: 2px solid var(--pgn-color-primary-base, #15376D);
  outline-offset: 2px;
}
.indigo-theme-settings .indigo-theme-color-card {
  background: var(--pgn-color-card-bg-base, var(--pgn-color-body-bg, transparent));
  border: 1px solid var(--pgn-color-border, var(--pgn-color-form-control-indicator-border, #c4c7cf));
  border-radius: .375rem;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  height: 100%;
  overflow: hidden;
  text-align: start;
}
.indigo-theme-settings .indigo-theme-color-card:hover {
  border-color: var(--pgn-color-primary-base, #15376D);
}
.indigo-theme-settings .indigo-theme-color-card--disabled {
  cursor: default;
  opacity: .65;
  pointer-events: none;
}
.indigo-theme-settings .indigo-theme-color-card:focus-visible {
  outline: 2px solid var(--pgn-color-primary-base, #15376D);
  outline-offset: 2px;
}
.indigo-theme-settings .indigo-theme-swatch {
  border-bottom: 1px solid var(--pgn-color-border, var(--pgn-color-form-control-indicator-border, #c4c7cf));
  height: 56px;
  position: relative;
  width: 100%;
}
.indigo-theme-settings .indigo-theme-swatch-edit {
  align-items: center;
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, .25);
  border-radius: 50%;
  bottom: .375rem;
  color: #111827;
  display: flex;
  height: 1.25rem;
  justify-content: center;
  position: absolute;
  right: .375rem;
  width: 1.25rem;
}
.indigo-theme-settings .indigo-theme-swatch-edit .pgn__icon {
  height: .75rem;
  width: .75rem;
}
.indigo-theme-settings .indigo-theme-hex {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: .75rem;
}
.indigo-theme-settings .indigo-theme-muted { opacity: .7; }
.indigo-theme-contrast {
  border-top: 1px solid var(--pgn-color-border, var(--pgn-color-form-control-indicator-border, #c4c7cf));
  margin-top: .75rem;
  padding-top: .75rem;
}
.indigo-theme-contrast-row {
  align-items: baseline;
  display: flex;
  gap: .5rem;
  justify-content: space-between;
}
.indigo-theme-picker { position: relative; }
.indigo-theme-picker .react-colorful { height: 200px; width: 100%; }
.indigo-theme-picker-guides {
  height: calc(100% - 36px);
  left: 0;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
}
.indigo-theme-picker-guides polyline {
  fill: none;
  opacity: .85;
  stroke: #FFFFFF;
  stroke-width: 1.5;
  vector-effect: non-scaling-stroke;
}
.indigo-theme-contrast-pass { color: var(--pgn-color-success-base, #0D7D4D); font-weight: 600; }
.indigo-theme-contrast-fail { color: var(--pgn-color-danger-base, #C32D3A); font-weight: 600; }
.indigo-theme-settings .indigo-theme-badge {
  background: transparent;
  border: 1px solid currentColor;
  color: var(--pgn-color-primary-base, #15376D);
  font-weight: 500;
}
#theme-settings .btn:disabled, #theme-settings .btn.disabled { opacity: .65; }
#theme-settings .btn-primary:disabled, #theme-settings .btn-primary.disabled {
  background-color: var(--pgn-color-btn-bg-primary, var(--pgn-color-primary-base, #15376D));
  border-color: var(--pgn-color-btn-bg-primary, var(--pgn-color-primary-base, #15376D));
  color: var(--pgn-color-btn-text-primary, #FFFFFF);
}
.pgn__modal-popup__tooltip { z-index: 1060; }
.indigo-theme-popover {
  background: var(--pgn-color-card-bg-base, var(--pgn-color-body-bg, #FFFFFF));
  border: 1px solid var(--pgn-color-border, var(--pgn-color-form-control-indicator-border, #c4c7cf));
  border-radius: .375rem;
  box-shadow: 0 .5rem 1.5rem rgba(0, 0, 0, .25);
  color: var(--pgn-color-text-base, inherit);
  padding: 1rem;
  width: 15rem;
}
.indigo-theme-popover .btn-link { color: var(--pgn-color-primary-base, #15376D); }
.indigo-theme-popover .react-colorful { height: 9rem; width: 100%; }
.indigo-theme-popover .react-colorful__saturation { border-radius: .25rem .25rem 0 0; }
.indigo-theme-popover .react-colorful__last-control { border-radius: 0 0 .25rem .25rem; }
`;

const ensureThemeSettingsCss = () => {
  try {
    if (document.getElementById(INDIGO_THEME_SETTINGS_STYLE_ID)) {
      return;
    }
    const style = document.createElement('style');
    style.id = INDIGO_THEME_SETTINGS_STYLE_ID;
    style.textContent = INDIGO_THEME_SETTINGS_CSS;
    document.head.appendChild(style);
  } catch (error) {}
};

const INDIGO_THEME_HEX_INPUT_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const normalizeHexInput = (value) => {
  const match = INDIGO_THEME_HEX_INPUT_RE.exec((value || '').trim());
  if (!match) {
    return null;
  }
  const digits = match[1];
  const expanded = digits.length === 3
    ? digits.split('').map((char) => char + char).join('')
    : digits;
  return `#${expanded.toUpperCase()}`;
};

const swatchStyle = (color) => ({ backgroundColor: color });

const ThemeSettings = () => {
  const intl = useIntl();
  const isDynamicThemeEnabled = getConfig().INDIGO_ENABLE_DYNAMIC_THEME;
  const authenticatedUser = getAuthenticatedUser();
  const apiUrl = `${getConfig().LMS_BASE_URL}${INDIGO_THEME_API_PATH}`;

  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSavedAlert, setShowSavedAlert] = useState(false);
  const [openToken, setOpenToken] = useState(null);
  const [pickerTarget, setPickerTarget] = useState(null);
  const [hexDraft, setHexDraft] = useState('');
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  const [baseTokenVersion, setBaseTokenVersion] = useState(0);
  const [contrastWarning, setContrastWarning] = useState('');

  const savedRef = useRef(null);
  const draftRef = useRef(null);
  const isDirtyRef = useRef(false);
  const cardElements = useRef({});

  const updateDraft = (next) => {
    draftRef.current = next;
    setDraft(next);
  };

  const messages = defineMessages({
    "theme.settings.heading": {
      id: "theme.settings.heading",
      defaultMessage: "Theme",
      description: "heading of the theme settings section",
    },
    "theme.settings.description": {
      id: "theme.settings.description",
      defaultMessage: "Choose how the site looks for you. Your choices only apply to your account.",
      description: "description of the theme settings section",
    },
    "theme.settings.preset.label": {
      id: "theme.settings.preset.label",
      defaultMessage: "Theme preset",
      description: "accessible label of the theme preset selector",
    },
    "theme.settings.colors.label": {
      id: "theme.settings.colors.label",
      defaultMessage: "Colors",
      description: "label of the theme colors sub-section",
    },
    "theme.settings.colors.help": {
      id: "theme.settings.colors.help",
      defaultMessage: "Click any color to change it. Colors are saved separately for Light and Dark.",
      description: "helper text of the theme colors sub-section",
    },
    "theme.settings.color.button": {
      id: "theme.settings.color.button",
      defaultMessage: "{label} color, {value}",
      description: "accessible label of a theme color card",
    },
    "theme.settings.color.modified": {
      id: "theme.settings.color.modified",
      defaultMessage: "Modified",
      description: "badge shown on a theme color that differs from the preset default",
    },
    "theme.settings.color.dialog": {
      id: "theme.settings.color.dialog",
      defaultMessage: "{label} color",
      description: "accessible name of the colour picker popover",
    },
    "theme.settings.color.hex": {
      id: "theme.settings.color.hex",
      defaultMessage: "Hex",
      description: "label of the hexadecimal colour input",
    },
    "theme.settings.color.hex.invalid": {
      id: "theme.settings.color.hex.invalid",
      defaultMessage: "Enter a color such as #15376D.",
      description: "error shown when the hexadecimal colour input is invalid",
    },
    "theme.settings.color.default": {
      id: "theme.settings.color.default",
      defaultMessage: "Use default",
      description: "label of the button that reverts one colour to the preset default",
    },
    "theme.settings.loading": {
      id: "theme.settings.loading",
      defaultMessage: "Loading theme settings",
      description: "screen reader text shown while theme settings load",
    },
    "theme.settings.load.error": {
      id: "theme.settings.load.error",
      defaultMessage: "We could not load your theme settings. Please try again later.",
      description: "error shown when theme settings fail to load",
    },
    "theme.settings.save": {
      id: "theme.settings.save",
      defaultMessage: "Save",
      description: "label of the button that saves the theme",
    },
    "theme.settings.saved": {
      id: "theme.settings.saved",
      defaultMessage: "Theme saved",
      description: "message shown after the theme was saved",
    },
    "theme.settings.save.error": {
      id: "theme.settings.save.error",
      defaultMessage: "We could not save your theme. Please try again.",
      description: "error shown when the theme could not be saved",
    },
    "theme.settings.reset": {
      id: "theme.settings.reset",
      defaultMessage: "Reset to default",
      description: "label of the button that resets the theme to its default",
    },
    "theme.settings.discard": {
      id: "theme.settings.discard",
      defaultMessage: "Discard changes",
      description: "label of the button that discards unsaved theme changes",
    },
    "theme.settings.contrast.ratio": {
      id: "theme.settings.contrast.ratio",
      defaultMessage: "Contrast ratio: {ratio}",
      description: "contrast ratio of the chosen colour against the page background",
    },
    "theme.settings.contrast.pass": {
      id: "theme.settings.contrast.pass",
      defaultMessage: "Pass",
      description: "shown when a colour meets a contrast level",
    },
    "theme.settings.contrast.fail": {
      id: "theme.settings.contrast.fail",
      defaultMessage: "Fail",
      description: "shown when a colour does not meet a contrast level",
    },
    "theme.settings.contrast.against": {
      id: "theme.settings.contrast.against",
      defaultMessage: "Measured against the {variant} page background.",
      description: "explains what the contrast ratio is measured against",
    },
    "theme.settings.contrast.against.text": {
      id: "theme.settings.contrast.against.text",
      defaultMessage: "Measured against Body text, which is shown on this color.",
      description: "explains that a surface colour is measured against the text drawn on it",
    },
    "theme.settings.contrast.warning": {
      id: "theme.settings.contrast.warning",
      defaultMessage: "Saved. {labels} may be hard to read against the page background — you can pick another value, or use the default.",
      description: "warning shown after saving colours that fall below the recommended contrast",
    },
    "theme.settings.reset.confirm": {
      id: "theme.settings.reset.confirm",
      defaultMessage: "Reset {preset} to its default colors? Your custom colors for this appearance will be lost.",
      description: "confirmation shown before resetting a preset",
    },
    "theme.settings.reset.confirm.action": {
      id: "theme.settings.reset.confirm.action",
      defaultMessage: "Yes, reset",
      description: "label of the button that confirms resetting a preset",
    },
    "theme.settings.reset.cancel": {
      id: "theme.settings.reset.cancel",
      defaultMessage: "Cancel",
      description: "label of the button that cancels resetting a preset",
    },
  });

  const overridesFromPayload = (payload) => {
    const result = {};
    (payload?.presets || []).forEach((preset) => {
      result[preset.slug] = { ...((payload.overrides || {})[preset.slug] || {}) };
    });
    Object.keys(payload?.overrides || {}).forEach((slug) => {
      if (!result[slug]) {
        result[slug] = { ...payload.overrides[slug] };
      }
    });
    return result;
  };

  const stableOverrides = (overrides) => {
    const result = {};
    Object.keys(overrides || {}).sort().forEach((slug) => {
      const entry = overrides[slug] || {};
      const inner = {};
      Object.keys(entry).sort().forEach((token) => { inner[token] = entry[token]; });
      result[slug] = inner;
    });
    return JSON.stringify(result);
  };

  const isDirty = Boolean(saved && draft
    && stableOverrides(draft.overrides) !== stableOverrides(overridesFromPayload(saved)));

  const adoptPayload = (payload) => {
    setSaved(payload);
    updateDraft({ preset: payload.preset, overrides: overridesFromPayload(payload) });
    setOpenToken(null);
  };

  const applySavedTheme = (payload) => {
    applyThemeTokens(effectiveTokensFor(payload, payload.preset));
    if (payload.variant) {
      persistThemeVariant(payload.variant);
    }
  };

  useEffect(() => { savedRef.current = saved; }, [saved]);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => { ensureThemeSettingsCss(); }, []);
  useEffect(() => onBaseTokensCaptured(() => setBaseTokenVersion((value) => value + 1)), []);

  useEffect(() => {
    if (!isDynamicThemeEnabled || !authenticatedUser) {
      return undefined;
    }

    let cancelled = false;
    fetchUserTheme()
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        adoptPayload(data);
        setIsLoading(false);
      })
      .catch((error) => {
        logError(error);
        if (!cancelled) {
          setHasLoadError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (isDirtyRef.current && savedRef.current) {
        applySavedTheme(savedRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showSavedAlert) {
      return undefined;
    }
    const timer = setTimeout(() => setShowSavedAlert(false), 5000);
    return () => clearTimeout(timer);
  }, [showSavedAlert]);

  const activePreset = (saved?.presets || []).find((preset) => preset.slug === draft?.preset);
  const presetTokens = useMemo(
    () => ({ ...(activePreset?.tokens || {}), ...baseTokensFor(activePreset?.variant) }),
    [activePreset, baseTokenVersion],
  );
  const activeOverrides = (draft?.overrides || {})[draft?.preset] || {};
  const availablePresets = (saved?.presets || []).filter(
    (preset) => getConfig().INDIGO_ENABLE_DARK_TOGGLE || preset.variant !== 'dark',
  );
  const savedActiveOverrides = (overridesFromPayload(saved) || {})[draft?.preset] || {};
  const canReset = Object.keys(savedActiveOverrides).length > 0;

  const overrideFor = (token) => activeOverrides[token];
  const colorFor = (token) => overrideFor(token) || presetTokens[token] || '#000000';
  const isModified = (token) => Boolean(overrideFor(token)) && overrideFor(token) !== presetTokens[token];

  const previewDraft = (nextDraft) => {
    applyThemeTokens(effectiveTokensFor(
      { ...(savedRef.current || saved), overrides: nextDraft.overrides },
      nextDraft.preset,
    ));
  };

  const applyDraftPreset = (nextDraft) => {
    const preset = ((savedRef.current || saved)?.presets || [])
      .find((item) => item.slug === nextDraft.preset);
    updateDraft(nextDraft);
    if (preset?.variant) {
      persistThemeVariant(preset.variant);
    }
    previewDraft(nextDraft);
  };

  const onPresetChange = (slug) => {
    if (!draft || slug === draft.preset || !(saved?.presets || []).some((item) => item.slug === slug)) {
      return;
    }
    applyDraftPreset({ ...draft, preset: slug });
    setOpenToken(null);
    setSubmitError('');
    setIsSubmitting(true);
    getAuthenticatedHttpClient()
      .patch(apiUrl, { preset: slug })
      .then(({ data }) => {
        setSaved(data);
        savedRef.current = data;
        writeThemeCache(data);
      })
      .catch((error) => {
        logError(error);
        setSubmitError(intl.formatMessage(messages["theme.settings.save.error"]));
        applyDraftPreset({ ...draftRef.current, preset: savedRef.current?.preset });
      })
      .finally(() => {
        resetUserThemeRequest();
        setIsSubmitting(false);
      });
  };

  const commitColor = (token, value) => {
    const nextDraft = {
      ...draft,
      overrides: {
        ...draft.overrides,
        [draft.preset]: { ...(draft.overrides[draft.preset] || {}), [token]: value },
      },
    };
    updateDraft(nextDraft);
    previewDraft(nextDraft);
  };

  const onPickerChange = (token, value) => {
    const normalized = normalizeHexInput(value);
    if (!normalized) {
      return;
    }
    setHexDraft(normalized);
    commitColor(token, normalized);
  };

  const onHexInputChange = (token, value) => {
    setHexDraft(value);
    const normalized = normalizeHexInput(value);
    if (normalized) {
      commitColor(token, normalized);
    }
  };

  const onHexInputBlur = () => {
    const normalized = normalizeHexInput(hexDraft);
    if (normalized) {
      setHexDraft(normalized);
    }
  };

  const onUseDefault = (token) => {
    const nextOverrides = { ...(draft.overrides[draft.preset] || {}) };
    delete nextOverrides[token];
    const nextDraft = {
      ...draft,
      overrides: { ...draft.overrides, [draft.preset]: nextOverrides },
    };
    updateDraft(nextDraft);
    setHexDraft(normalizeHexInput(presetTokens[token]) || '');
    previewDraft(nextDraft);
  };

  const onOpenPicker = (token) => {
    setHexDraft(normalizeHexInput(colorFor(token)) || colorFor(token));
    setPickerTarget(cardElements.current[token] || null);
    setOpenToken(token);
  };

  const contrastBackground = () => (activePreset?.variant === 'dark' ? '#0D0D0E' : '#FFFFFF');

  const contrastLevelsFor = () => [
    { label: 'AA', min: INDIGO_CONTRAST_AA },
    { label: 'AAA', min: 7 },
  ];

  const contrastPeerFor = (token) => {
    if (token !== '--pgn-color-primary-light') {
      return { color: contrastBackground(), onText: false };
    }
    const text = activeOverrides['--pgn-color-text-base']
      || presetTokens['--pgn-color-text-base'];
    return { color: text || contrastBackground(), onText: Boolean(text) };
  };

  const pickerGuides = (token, color) => {
    const levels = contrastLevelsFor(token);
    const rgb = hexToRgb(color);
    if (!levels.length || !rgb) {
      return [];
    }
    const { h } = rgbToHsv(rgb);
    const peer = contrastPeerFor(token);
    return levels
      .map((level) => ({
        label: level.label,
        points: contrastGuidePoints(h, level.min, peer.color),
      }))
      .filter((guide) => guide.points);
  };

  const contrastReport = (token, color) => {
    const levels = contrastLevelsFor(token);
    const peer = contrastPeerFor(token);
    const ratio = contrastRatio(color, peer.color);
    if (!levels.length || !ratio) {
      return null;
    }
    return {
      ratio: Math.round(ratio * 100) / 100,
      onText: peer.onText,
      levels: levels.map((level) => ({ ...level, pass: ratio >= level.min })),
    };
  };

  const openGuides = useMemo(
    () => (openToken ? pickerGuides(openToken, colorFor(openToken)) : []),
    [openToken, activeOverrides, presetTokens, activePreset],
  );

  const contrastFailures = () => INDIGO_THEME_TOKENS
    .filter((entry) => {
      if (!activeOverrides[entry.token]) {
        return false;
      }
      const report = contrastReport(entry.token, activeOverrides[entry.token]);
      return report && !report.levels[0].pass;
    })
    .map((entry) => tokenLabel(entry));

  const onSave = () => {
    const failures = contrastFailures();
    setIsSubmitting(true);
    setSubmitError('');
    setContrastWarning('');
    setOpenToken(null);
    getAuthenticatedHttpClient()
      .put(apiUrl, { preset: draft.preset, overrides: draft.overrides })
      .then(({ data }) => {
        adoptPayload(data);
        writeThemeCache(data);
        applyThemeTokens(effectiveTokensFor(data, data.preset));
        if (failures.length) {
          setContrastWarning(intl.formatMessage(
            messages["theme.settings.contrast.warning"],
            { labels: failures.join(', ') },
          ));
        } else {
          setShowSavedAlert(true);
        }
      })
      .catch((error) => {
        logError(error);
        setSubmitError(intl.formatMessage(messages["theme.settings.save.error"]));
      })
      .finally(() => {
        resetUserThemeRequest();
        setIsSubmitting(false);
      });
  };

  const onReset = () => {
    setIsConfirmingReset(false);
    setIsSubmitting(true);
    setSubmitError('');
    setOpenToken(null);
    const cleared = { ...overridesFromPayload(saved), [draft.preset]: {} };
    getAuthenticatedHttpClient()
      .put(apiUrl, { preset: draft.preset, overrides: cleared })
      .then(({ data }) => {
        adoptPayload(data);
        writeThemeCache(data);
        applySavedTheme(data);
        setShowSavedAlert(true);
      })
      .catch((error) => {
        logError(error);
        setSubmitError(intl.formatMessage(messages["theme.settings.save.error"]));
      })
      .finally(() => {
        resetUserThemeRequest();
        setIsSubmitting(false);
      });
  };

  const onDiscard = () => {
    updateDraft({ preset: saved.preset, overrides: overridesFromPayload(saved) });
    setOpenToken(null);
    setSubmitError('');
    applySavedTheme(saved);
  };

  if (!isDynamicThemeEnabled || !authenticatedUser) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="indigo-theme-settings mt-5" id="theme-settings">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages["theme.settings.loading"])} />
      </div>
    );
  }

  if (hasLoadError || !saved || !draft) {
    return (
      <div className="indigo-theme-settings mt-5" id="theme-settings">
        <Alert variant="danger">
          {intl.formatMessage(messages["theme.settings.load.error"])}
        </Alert>
      </div>
    );
  }

  const tokenLabel = (entry) => (INDIGO_TOKEN_MESSAGES[entry.token]
    ? intl.formatMessage(INDIGO_TOKEN_MESSAGES[entry.token])
    : entry.label);
  const isHexInvalid = hexDraft !== '' && !normalizeHexInput(hexDraft);
  const tileClassName = (slug) => (slug === draft.preset
    ? 'indigo-theme-tile indigo-theme-tile--selected'
    : 'indigo-theme-tile');
  const colorCardClassName = isSubmitting
    ? 'indigo-theme-color-card indigo-theme-color-card--disabled'
    : 'indigo-theme-color-card';

  return (
    <div className="indigo-theme-settings mt-5" id="theme-settings">
      <h2 className="section-heading h4 mb-2">
        {intl.formatMessage(messages["theme.settings.heading"])}
      </h2>
      <p className="small indigo-theme-muted mb-4">
        {intl.formatMessage(messages["theme.settings.description"])}
      </p>

      {showSavedAlert && (
        <Alert variant="success" onClose={() => setShowSavedAlert(false)} dismissible>
          {intl.formatMessage(messages["theme.settings.saved"])}
        </Alert>
      )}
      {submitError && (
        <Alert variant="danger" onClose={() => setSubmitError('')} dismissible>
          {submitError}
        </Alert>
      )}
      {contrastWarning && (
        <Alert variant="warning" onClose={() => setContrastWarning('')} dismissible>
          {contrastWarning}
        </Alert>
      )}

      <Form.RadioSet
        name="indigo-theme-preset"
        value={draft.preset}
        onChange={(event) => onPresetChange(event.target.value)}
        isInline
        aria-label={intl.formatMessage(messages["theme.settings.preset.label"])}
        className="indigo-theme-tiles mb-4"
      >
        {availablePresets.map((preset) => (
          <Form.Radio
            key={preset.slug}
            value={preset.slug}
            disabled={isSubmitting}
            className={tileClassName(preset.slug)}
          >
            <Icon src={preset.variant === 'dark' ? Nightlight : WbSunny} className="mr-2" />
            <span className="font-weight-bold indigo-theme-tile-name">{preset.name}</span>
          </Form.Radio>
        ))}
      </Form.RadioSet>

      <div className="font-weight-bold small mb-1">
        {intl.formatMessage(messages["theme.settings.colors.label"])}
      </div>
      <p className="small indigo-theme-muted mb-3">
        {intl.formatMessage(messages["theme.settings.colors.help"])}
      </p>

      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 mb-4">
        {INDIGO_THEME_TOKENS.map((entry) => (
          <div
            className="col mb-3"
            key={entry.token}
            ref={(element) => { cardElements.current[entry.token] = element; }}
          >
            <Card
              isClickable={!isSubmitting}
              role="button"
              aria-disabled={isSubmitting}
              aria-label={intl.formatMessage(messages["theme.settings.color.button"], {
                label: tokenLabel(entry),
                value: colorFor(entry.token),
              })}
              className={colorCardClassName}
              onClick={() => {
                if (!isSubmitting) {
                  onOpenPicker(entry.token);
                }
              }}
              onKeyDown={(event) => {
                if (!isSubmitting && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onOpenPicker(entry.token);
                }
              }}
            >
              <div className="indigo-theme-swatch" style={swatchStyle(colorFor(entry.token))}>
                <span className="indigo-theme-swatch-edit" aria-hidden="true">
                  <Icon src={Edit} />
                </span>
              </div>
              <div className="p-2">
                <div className="small font-weight-bold">{tokenLabel(entry)}</div>
                <div className="indigo-theme-hex indigo-theme-muted">{colorFor(entry.token)}</div>
                {isModified(entry.token) && (
                  <Badge className="indigo-theme-badge mt-1">
                    {intl.formatMessage(messages["theme.settings.color.modified"])}
                  </Badge>
                )}
              </div>
            </Card>
            {openToken === entry.token && pickerTarget && !isSubmitting && (
              <ModalPopup
                positionRef={pickerTarget}
                isOpen
                onClose={() => setOpenToken(null)}
                placement="bottom-start"
              >
                <div
                  className="indigo-theme-popover"
                  role="dialog"
                  aria-modal="false"
                  aria-label={intl.formatMessage(messages["theme.settings.color.dialog"], {
                    label: tokenLabel(entry),
                  })}
                >
                  <div className="indigo-theme-picker">
                    <HexColorPicker
                      color={colorFor(entry.token)}
                      onChange={(value) => onPickerChange(entry.token, value)}
                    />
                    <svg
                      className="indigo-theme-picker-guides"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      {openGuides.map((guide) => (
                        <polyline key={guide.label} points={guide.points} />
                      ))}
                    </svg>
                  </div>
                  <Form.Group className="mt-3 mb-0" isInvalid={isHexInvalid}>
                    <Form.Label className="small mb-1">
                      {intl.formatMessage(messages["theme.settings.color.hex"])}
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={hexDraft}
                      disabled={isSubmitting}
                      spellCheck={false}
                      autoComplete="off"
                      onChange={(event) => onHexInputChange(entry.token, event.target.value)}
                      onBlur={onHexInputBlur}
                    />
                    {isHexInvalid && (
                      <Form.Control.Feedback type="invalid">
                        {intl.formatMessage(messages["theme.settings.color.hex.invalid"])}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  {(() => {
                    const report = contrastReport(entry.token, colorFor(entry.token));
                    if (!report) {
                      return null;
                    }
                    return (
                      <div className="indigo-theme-contrast small">
                        <div className="font-weight-bold mb-1">
                          {intl.formatMessage(
                            messages["theme.settings.contrast.ratio"],
                            { ratio: report.ratio.toFixed(2) },
                          )}
                        </div>
                        {report.levels.map((level) => (
                          <div className="indigo-theme-contrast-row" key={level.label}>
                            <span>{`${level.label}: ${level.min}`}</span>
                            <span className={level.pass
                              ? 'indigo-theme-contrast-pass'
                              : 'indigo-theme-contrast-fail'}
                            >
                              {intl.formatMessage(messages[level.pass
                                ? "theme.settings.contrast.pass"
                                : "theme.settings.contrast.fail"])}
                            </span>
                          </div>
                        ))}
                        <div className="indigo-theme-muted mt-1">
                          {report.onText
                            ? intl.formatMessage(messages["theme.settings.contrast.against.text"])
                            : intl.formatMessage(
                              messages["theme.settings.contrast.against"],
                              { variant: activePreset?.name || '' },
                            )}
                        </div>
                      </div>
                    );
                  })()}
                  <Button
                    variant="link"
                    size="sm"
                    className="px-0 mt-2"
                    disabled={isSubmitting}
                    onClick={() => onUseDefault(entry.token)}
                  >
                    {intl.formatMessage(messages["theme.settings.color.default"])}
                  </Button>
                </div>
              </ModalPopup>
            )}
          </div>
        ))}
      </div>

      {isConfirmingReset && (
        <Alert variant="warning">
          <p className="mb-3">
            {intl.formatMessage(
              messages["theme.settings.reset.confirm"],
              { preset: activePreset?.name || '' },
            )}
          </p>
          <ActionRow>
            <Button variant="tertiary" onClick={() => setIsConfirmingReset(false)} disabled={isSubmitting}>
              {intl.formatMessage(messages["theme.settings.reset.cancel"])}
            </Button>
            <Button variant="danger" onClick={onReset} disabled={isSubmitting}>
              {intl.formatMessage(messages["theme.settings.reset.confirm.action"])}
            </Button>
          </ActionRow>
        </Alert>
      )}

      <ActionRow>
        <Button
          variant="outline-primary"
          onClick={() => setIsConfirmingReset(true)}
          disabled={isSubmitting || !canReset || isConfirmingReset}
        >
          {intl.formatMessage(messages["theme.settings.reset"])}
        </Button>
        <ActionRow.Spacer />
        {isDirty && (
          <Button variant="tertiary" onClick={onDiscard} disabled={isSubmitting}>
            {intl.formatMessage(messages["theme.settings.discard"])}
          </Button>
        )}
        <Button variant="primary" onClick={onSave} disabled={isSubmitting || !isDirty}>
          {intl.formatMessage(messages["theme.settings.save"])}
        </Button>
      </ActionRow>
    </div>
  );
};
  addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'indigo_footer',
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: IndigoFooter,
        },
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'indigo_user_theme',
            type: DIRECT_PLUGIN,
            priority: 2,
            RenderWidget: ApplyUserTheme,
        },
    },
]);
  addPlugins(config, 'desktop_secondary_menu_slot', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'theme_switch_button',
            type: DIRECT_PLUGIN,
            RenderWidget: ToggleThemeButton,
        },
    },
]);
  addPlugins(config, 'logo_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'custom_logo',
            type: DIRECT_PLUGIN,
            RenderWidget: ThemedLogo,
        }
    }
]);
if (config.pluginSlots && config.pluginSlots['logo_slot']) {
  config.pluginSlots['logo_slot'].keepDefault = false;

  const logoInsertPlugins =
    config.pluginSlots['logo_slot'].plugins.filter(plugin => plugin.op === 'insert' && plugin.widget?.id === 'custom_logo');

  if (logoInsertPlugins.length > 1) {
    let kept = false;
    config.pluginSlots['logo_slot'].plugins =
      config.pluginSlots['logo_slot'].plugins.filter(plugin => {
        if (plugin.op === 'insert' && plugin.widget?.id === 'custom_logo') {
          if (kept) return false;
          kept = true;
        }
        return true;
      });
  }
}

if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const indigoFooterWidgetIds = ['indigo_footer', 'read_theme_cookie', 'indigo_user_theme'];

  const foreignFooterPlugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(
      plugin => plugin.op === 'insert' && !indigoFooterWidgetIds.includes(plugin.widget?.id));

  if (foreignFooterPlugins.length > 0) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'indigo_footer';
      }
      return true;
    });
  }
}

  return config;
}

export default setConfig;